import { TAETIGKEIT_ARTEN, type SifaTaetigkeit, type TaetigkeitArt } from './betreuungTypes';

/**
 * Taetigkeitsnachweis (Betreuungszeiten) — REINE Logik, kein Supabase.
 * Summen, Gruppierungen, Soll/Ist gegen die Einsatzzeit aus `metadata.sifa`
 * und die Umrechnung Minuten ↔ Anzeige. Die Datenbank fuehrt MINUTEN
 * (`dauer_minuten`, CHECK 1–1440); Stunden gibt es nur in der Anzeige.
 */

/** Was der Aufrufer beim Anlegen einer Taetigkeit setzen darf (Bauplan §2). */
export type NeueTaetigkeit = Pick<SifaTaetigkeit, 'datum' | 'dauerMinuten' | 'art' | 'titel'> &
  Partial<Pick<SifaTaetigkeit, 'beschreibung' | 'durchgefuehrtVon' | 'bezug'>>;

/** Hoechste Dauer je Taetigkeit (ein Tag) — Spiegel des CHECKs in Nr. 92. */
export const DAUER_MAX_MINUTEN = 1440;

const minutenVon = (t: Pick<SifaTaetigkeit, 'dauerMinuten'>): number =>
  Number.isFinite(t.dauerMinuten) && t.dauerMinuten > 0 ? t.dauerMinuten : 0;

/** Summe der Minuten; unlesbare oder negative Werte zaehlen 0. */
export const summeMinuten = (list: readonly Pick<SifaTaetigkeit, 'dauerMinuten'>[]): number =>
  list.reduce((s, t) => s + minutenVon(t), 0);

export interface MonatsGruppe<T> {
  /** yyyy-mm */
  monat: string;
  minuten: number;
  anzahl: number;
  eintraege: T[];
}

/**
 * Gruppiert je Kalendermonat (yyyy-mm aus `datum`), chronologisch aufsteigend.
 * Eintraege ohne lesbares Datum landen unter „?" am Ende — sie verschwinden
 * nicht, sonst stimmte die Summe der Gruppen nicht mit `summeMinuten` ueberein.
 */
export const gruppiereJeMonat = <T extends Pick<SifaTaetigkeit, 'datum' | 'dauerMinuten'>>(list: readonly T[]): MonatsGruppe<T>[] => {
  const karte = new Map<string, MonatsGruppe<T>>();
  for (const t of list) {
    const m = /^(\d{4})-(\d{2})/.exec(t.datum || '');
    const monat = m ? `${m[1]}-${m[2]}` : '?';
    let g = karte.get(monat);
    if (!g) {
      g = { monat, minuten: 0, anzahl: 0, eintraege: [] };
      karte.set(monat, g);
    }
    g.minuten += minutenVon(t);
    g.anzahl += 1;
    g.eintraege.push(t);
  }
  return [...karte.values()].sort((a, b) => (a.monat === '?' ? 1 : b.monat === '?' ? -1 : a.monat.localeCompare(b.monat)));
};

export interface ArtGruppe {
  art: TaetigkeitArt;
  label: string;
  minuten: number;
  anzahl: number;
}

/** Anzeigename einer Art; eine unbekannte Art zeigt ihren Rohwert. */
export const artLabel = (art: string): string => TAETIGKEIT_ARTEN.find((a) => a.id === art)?.label ?? art;

/** Gruppiert je Art, groesste Summe zuerst; Arten ohne Eintrag fehlen. */
export const gruppiereJeArt = (list: readonly Pick<SifaTaetigkeit, 'art' | 'dauerMinuten'>[]): ArtGruppe[] => {
  const karte = new Map<TaetigkeitArt, ArtGruppe>();
  for (const t of list) {
    let g = karte.get(t.art);
    if (!g) {
      g = { art: t.art, label: artLabel(t.art), minuten: 0, anzahl: 0 };
      karte.set(t.art, g);
    }
    g.minuten += minutenVon(t);
    g.anzahl += 1;
  }
  return [...karte.values()].sort((a, b) => b.minuten - a.minuten || a.label.localeCompare(b.label, 'de'));
};

export interface SollIstVergleich {
  /** Geleistete Stunden (Minuten / 60, zwei Nachkommastellen). */
  istStunden: number;
  /** Vereinbarte Einsatzzeit je Jahr; 0, wenn nicht gepflegt. */
  sollStunden: number;
  /** Ist in Prozent des Solls, gerundet; 0 ohne Soll. Darf ueber 100 liegen. */
  prozent: number;
  /** Was bis zum Soll noch fehlt, nie negativ. */
  restStunden: number;
}

const rund2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Ist gegen Soll. Ohne gepflegtes Soll (null, 0, unlesbar) bleibt `prozent` 0
 * und `restStunden` 0 — die Anzeige sagt dann „Soll nicht hinterlegt" statt
 * eine Zahl zu erfinden.
 */
export const sollIstVergleich = (minutenJahr: number, sollStunden: number | null | undefined): SollIstVergleich => {
  const ist = rund2(Math.max(0, Number.isFinite(minutenJahr) ? minutenJahr : 0) / 60);
  const soll = typeof sollStunden === 'number' && Number.isFinite(sollStunden) && sollStunden > 0 ? rund2(sollStunden) : 0;
  return {
    istStunden: ist,
    sollStunden: soll,
    prozent: soll > 0 ? Math.round((ist / soll) * 100) : 0,
    restStunden: soll > 0 ? rund2(Math.max(0, soll - ist)) : 0,
  };
};

/**
 * „1 h 30 min", „2 h", „45 min", „0 min". Sekundenbruchteile werden auf ganze
 * Minuten gerundet; Unsinn (NaN, negativ) wird „0 min".
 */
export const formatDauer = (minuten: number): string => {
  const m = Number.isFinite(minuten) && minuten > 0 ? Math.round(minuten) : 0;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h === 0) return `${rest} min`;
  if (rest === 0) return `${h} h`;
  return `${h} h ${rest} min`;
};

/**
 * Dauer aus einer Eingabe in Minuten:
 *   „90" / „90 min"          → 90
 *   „1:30"                   → 90  (Doppelpunkt = Stunden:Minuten)
 *   „1,5 h" / „1.5" / „1,5"  → 90  (Komma oder Punkt = Dezimalstunden, sobald
 *                                   ein Bruch oder ein „h" dabei ist)
 *   „1 h 30 min" / „1h30"    → 90
 *   „2 h" / „2h"             → 120
 * Eine nackte ganze Zahl ist immer MINUTEN („90" ist keine 90 Stunden).
 * null bei leer, unlesbar oder Ergebnis <= 0 — ein Formular zeigt dann
 * „Dauer bitte als Minuten, 1:30 oder 1,5 h eingeben".
 */
export const parseDauer = (text: string | null | undefined): number | null => {
  const s = (text ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!s) return null;
  const zahl = (x: string): number => Number(x.replace(',', '.'));
  const ergebnis = (minuten: number): number | null => {
    const m = Math.round(minuten);
    return Number.isFinite(m) && m > 0 ? m : null;
  };

  // 1:30 → Stunden:Minuten
  let m = /^(\d{1,3}):(\d{1,2})\s*(h|std\.?|stunden?)?$/.exec(s);
  if (m) return ergebnis(Number(m[1]) * 60 + Number(m[2]));

  // 1 h 30 min / 1h30 / 1 h 30
  m = /^(\d{1,3})\s*(h|std\.?|stunden?)\s*(\d{1,2})\s*(min\.?|minuten)?$/.exec(s);
  if (m) return ergebnis(Number(m[1]) * 60 + Number(m[3]));

  // 1,5 h / 2 h / 1.5 std
  m = /^(\d{1,3}(?:[.,]\d{1,2})?)\s*(h|std\.?|stunden?)$/.exec(s);
  if (m) return ergebnis(zahl(m[1]) * 60);

  // 45 min / 45
  m = /^(\d{1,4})\s*(min\.?|minuten)?$/.exec(s);
  if (m) return ergebnis(Number(m[1]));

  // 1,5 / 1.5 ohne Einheit: ein Bruch sind Stunden — niemand meint 1,5 Minuten.
  m = /^(\d{1,3}[.,]\d{1,2})$/.exec(s);
  if (m) return ergebnis(zahl(m[1]) * 60);

  return null;
};
